import { useState, useEffect, useRef, useCallback } from 'react';

const getWebSocketUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (!backendUrl) return null;
  return backendUrl.replace(/^http/, 'ws');
};

export const useWebSocket = (endpoint, options = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const isUnmountedRef = useRef(false);

  const { 
    onMessage, 
    onOpen, 
    onClose, 
    onError
  } = options;

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof message === 'string' ? message : JSON.stringify(message));
    }
  }, []);

  useEffect(() => {
    // Skip if no endpoint
    if (!endpoint) return;

    const wsBaseUrl = getWebSocketUrl();
    if (!wsBaseUrl) return;

    isUnmountedRef.current = false;

    const wsUrl = `${wsBaseUrl}${endpoint}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = (event) => {
        if (isUnmountedRef.current) return;
        setIsConnected(true);
        setError(null);
        onOpen?.(event);
      };

      ws.onmessage = (event) => {
        if (isUnmountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          onMessage?.(data);
        } catch (e) {
          setLastMessage(event.data);
          onMessage?.(event.data);
        }
      };

      ws.onclose = (event) => {
        if (isUnmountedRef.current) return;
        setIsConnected(false);
        onClose?.(event);
      };

      ws.onerror = (event) => {
        if (isUnmountedRef.current) return;
        setError('WebSocket connection error');
        onError?.(event);
      };
    } catch (err) {
      // This setState in catch is intentional for error handling
      setError(err.message);
    }

    return () => {
      isUnmountedRef.current = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [endpoint, onMessage, onOpen, onClose, onError]);

  return {
    isConnected,
    lastMessage,
    error,
    sendMessage,
    disconnect
  };
};

export const usePriceFeed = (enabled = true) => {
  const [prices, setPrices] = useState({});
  
  const { isConnected, error } = useWebSocket(enabled ? '/ws/prices' : null, {
    onMessage: (data) => {
      if (data?.type === 'price_feed') {
        setPrices(data.data);
      }
    }
  });

  if (!enabled) {
    return { prices: {}, isConnected: false, error: null };
  }

  return { prices, isConnected, error };
};

export const usePositionsStream = (tenantId) => {
  const [positions, setPositions] = useState([]);
  const [priceTicks, setPriceTicks] = useState({});

  const { isConnected, error } = useWebSocket(tenantId ? `/ws/positions/${tenantId}` : null, {
    onMessage: (data) => {
      if (data?.type === 'positions_update') {
        setPositions(data.data);
      } else if (data?.type === 'price_tick') {
        setPriceTicks(prev => ({
          ...prev,
          [data.data.symbol]: data.data
        }));
      }
    }
  });

  return { positions, priceTicks, isConnected, error };
};

export default useWebSocket;
